import React, { useState, useEffect } from 'react';
import { db_ppp } from '../../config/firebase_ppp'; 
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { IconZap } from './Icons';

export const RealTimeBasket = () => {
    const [data, setData] = useState<any[]>([]);
    const [latestItem, setLatestItem] = useState<any>(null);

    useEffect(() => {
        // Increase limit to 300 to get a better statistical average
        const q = query(collection(db_ppp, "item_prices"), orderBy("timestamp", "desc"), limit(300));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rawData: any[] = [];
            snapshot.forEach((doc) => rawData.push(doc.data()));

            if (rawData.length > 0) setLatestItem(rawData[0]);

            // Group by Date
            const groupedData: Record<string, { milkSum: number, milkCount: number, eggSum: number, eggCount: number }> = {};

            // Sort Chronologically for the chart
            rawData.sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));

            rawData.forEach(item => {
                // Normalize Date
                let dateKey = item.date; 
                // If date format varies, you might need parsing logic here. 
                // Assuming MM/DD/YYYY for now based on ingestion script.

                const desc = item.description.toLowerCase();
                const price = item.price;

                // --- SMART FILTERING ---
                // Exclude "Organic", "Premium", "Almond", "Soy" to track BASE INFLATION
                if (desc.includes('organic') || desc.includes('almond') || desc.includes('soy')) {
                    return; 
                }

                if (!groupedData[dateKey]) {
                    groupedData[dateKey] = { milkSum: 0, milkCount: 0, eggSum: 0, eggCount: 0 };
                }

                if (desc.includes('milk')) {
                    groupedData[dateKey].milkSum += price;
                    groupedData[dateKey].milkCount += 1;
                } else if (desc.includes('egg')) {
                    groupedData[dateKey].eggSum += price;
                    groupedData[dateKey].eggCount += 1;
                }
            });

            // Calculate Averages
            const chartData = Object.keys(groupedData).map(date => {
                const day = groupedData[date];
                return {
                    date: date.slice(0, 5), // "MM/DD"
                    // Round to 2 decimal places
                    Milk: day.milkCount > 0 ? parseFloat((day.milkSum / day.milkCount).toFixed(2)) : null,
                    Eggs: day.eggCount > 0 ? parseFloat((day.eggSum / day.eggCount).toFixed(2)) : null,
                    // Add count for tooltip context
                    _ctx: { milkCount: day.milkCount, eggCount: day.eggCount }
                };
            });

            setData(chartData);
        });

        return () => unsubscribe();
    }, []);

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-full flex flex-col">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <IconZap className="text-amber-500" /> PricePlanPro Index
                    </h3>
                    <p className="text-xs text-slate-500">Real-time commodity basket (Excl. Organic/Premium)</p>
                </div>
                
                {latestItem && (
                    <div className="text-right">
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Live Feed</span>
                        <div className="text-xs font-bold text-slate-700">
                            {latestItem.description} <span className="text-emerald-600">${latestItem.price.toFixed(2)}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex-1 min-h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{fontSize: 10}} />
                        <YAxis 
                            domain={['auto', 'auto']} 
                            tick={{fontSize: 10}} 
                            tickFormatter={(val) => `$${val}`} 
                        />
                        <Tooltip 
                            contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'}}
                            formatter={(value: any, name: any, props: any) => {
                                // Show sample size in tooltip
                                const count = name === 'Milk' ? props.payload._ctx.milkCount : props.payload._ctx.eggCount;
                                return [`$${value} (n=${count})`, name];
                            }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="Milk" stroke="#3b82f6" strokeWidth={3} dot={{r: 3}} connectNulls />
                        <Line type="monotone" dataKey="Eggs" stroke="#f59e0b" strokeWidth={3} dot={{r: 3}} connectNulls />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};